<template>
  <div class="faq-page">
    <div class="faq-hero">
      <div class="container">
        <h1>{{ $t('faqPage.title') || 'Frequently Asked Questions' }}</h1>
        <p>{{ $t('faqPage.subtitle') || 'Everything you need to know about WholeLifeGoals' }}</p>
      </div>
    </div>

    <div class="faq-body">
      <div class="container">
        <div v-if="loading" class="loading-message">Loading...</div>

        <div v-else class="faq-container">
          <div v-for="(item, index) in faqItems" :key="item.id || index" class="faq-item">
            <div class="faq-question" :class="{ active: expandedFaq === index }" @click="toggle(index)">
              <span>{{ item.question }}</span>
              <span class="faq-icon">+</span>
            </div>
            <div v-show="expandedFaq === index" class="faq-answer" v-html="parseMarkdown(item.answer)" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { marked } from 'marked';

export default {
  name: 'FaqPage',
  data() {
    return {
      expandedFaq: null,
      loading: false,
      faqItems: []
    };
  },
  mounted() {
    this.fetchFaqItems();
  },
  methods: {
    toggle(index) {
      this.expandedFaq = this.expandedFaq === index ? null : index;
    },
    parseMarkdown(content) {
      return marked.parse(content || '');
    },
    async fetchFaqItems() {
      this.loading = true;
      try {
        const strapiUrl = process.env.VUE_APP_STRAPI_URL || 'http://localhost:1342';
        const response = await axios.get(`${strapiUrl}/api/pages`, {
          params: {
            'filters[pageId][$eq]': 'faq-page',
            populate: '*'
          }
        });

        const page = response.data?.data?.[0];
        if (page?.faqItems?.length) {
          this.faqItems = page.faqItems;
        }
      } catch (err) {
        console.error('Error fetching FAQ items from Strapi:', err);
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
@import '@/assets/_variables.scss';

.faq-page {
  width: 100%;
  background: $bg-white;
  color: $text-dark;
  font-family: $font-family-body;
  line-height: 1.6;
}

.container {
  max-width: 860px;
  margin: 0 auto;
  padding: 0 2rem;

  @media (max-width: 768px) {
    padding: 0 1.5rem;
  }
}

/* Hero */
.faq-hero {
  background: linear-gradient(135deg, $brand-color, $brand-dark);
  color: white;
  padding: 5rem 0;
  text-align: center;

  h1 {
    font-size: 3rem;
    font-weight: 700;
    margin-bottom: 2rem;
    line-height: 1.1;
    border-bottom: none !important;
    padding-bottom: 0 !important;

    @media (max-width: 768px) {
      font-size: 2.2rem;
    }
  }

  p {
    font-size: 1.15rem;
    opacity: 0.9;
    max-width: 500px;
    margin: 0 auto;
  }
}

/* Body */
.faq-body {
  padding: 2rem 0;
}

.loading-message {
  text-align: center;
  padding: 3rem 0;
  color: $text-light;
  font-size: 1.1rem;
}

/* Accordion */
.faq-container {
  display: flex;
  flex-direction: column;
}

.faq-item {
  border-bottom: 1px solid $border;
  padding: 1.25rem 0;

  &:first-child {
    border-top: 1px solid $border;
  }
}

.faq-question {
  width: 100%;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0;
  font-size: 1rem;
  font-weight: 600;
  color: $text-dark;
  transition: color 0.3s ease;
  user-select: none;

  &:hover {
    color: $brand-color;
  }

  &.active {
    color: $brand-color;
  }

  .faq-icon {
    font-size: 1.5rem;
    transition: transform 0.3s ease;
    flex-shrink: 0;
  }

  &.active .faq-icon {
    transform: rotate(45deg);
  }
}

.faq-answer {
  padding: 1rem 0 0.25rem;
  color: $text-light;
  line-height: 1.8;
  font-size: 0.95rem;

  ::v-deep {
    a {
      color: $brand-color;
      text-decoration: none;
      border-bottom: 1px solid currentColor;

      &:hover {
        color: $brand-dark;
      }
    }

    p {
      margin-bottom: 0.75rem;

      &:last-child {
        margin-bottom: 0;
      }
    }

    ul,
    ol {
      padding-left: 1.5rem;
      margin-bottom: 0.75rem;
    }

    li {
      margin-bottom: 0.4rem;
    }

    strong {
      color: $text-dark;
      font-weight: 600;
    }
  }
}
</style>
